import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type { PlacementPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";

export default function PlacementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [placement, setPlacement] = useState<PlacementPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getPlacement(id)
      .then((res) => setPlacement(res.placement))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (!id || !confirm("Delete this placement? This action cannot be undone.")) return;
    try {
      await api.deletePlacement(id);
      navigate("/placements");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete placement");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="placement-detail" data-state="loading">Loading...</p>;
  if (error && !placement) return <p className="text-sm text-destructive" data-page="placement-detail" data-state="error" data-error>{error}</p>;
  if (!placement) return <p className="text-muted-foreground" data-page="placement-detail" data-state="empty">Placement not found</p>;

  return (
    <div className="flex flex-col gap-6" data-page="placement-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div className="flex items-center gap-3">
          <ListBackButton to="/placements" label="Placements" />
          <div>
            <h1 className="text-2xl font-semibold">{placement.sponsor_name}</h1>
            <p className="text-muted-foreground">
              {placement.ad_space_name} -- {placement.court_name} ({placement.school_name})
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {placement._actions.can_edit && (
            <Link
              to={`/placements/${placement.id}/edit`}
              className={buttonVariants({ variant: "outline" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          )}
          {placement._actions.can_delete && (
            <Button variant="destructive" onClick={handleDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Status</p>
          <p className="font-medium capitalize">{placement.status}</p>
        </div>
        <div className="rounded-lg border border-border p-4">
          <p className="text-sm text-muted-foreground">Period</p>
          <p className="font-medium">{placement.start_date} -- {placement.end_date}</p>
        </div>
        {placement.package_name && (
          <div className="rounded-lg border border-border p-4">
            <p className="text-sm text-muted-foreground">Package</p>
            <p className="font-medium">{placement.package_name}</p>
          </div>
        )}
        {placement.notes && (
          <div className="rounded-lg border border-border p-4">
            <p className="text-sm text-muted-foreground">Notes</p>
            <p className="font-medium">{placement.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
